import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Building, Calendar, MapPin } from 'lucide-react';
import DatePicker from './DatePicker';
import { api } from '../utils/api';
import { toast } from 'sonner';

const AddRegistrationDialog = ({ open, onClose, onSuccess, state }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    registration_type: '',
    dba_name: '',
    registration_number: '',
    registration_date: '',
    expiration_date: '',
    registration_fee: '',
    registered_agent: '',
    agent_address: '',
    agent_city: '',
    agent_state: '',
    agent_zip: '',
    principal_address: '',
    principal_city: '',
    principal_state: '',
    principal_zip: '',
    status: 'active'
  });

  const registrationTypes = [
    { value: 'DBA', label: 'DBA (Doing Business As)' },
    { value: 'Foreign Corporation', label: 'Foreign Corporation' }
  ];

  const handleDateChange = (field, value) => {
    // Format as MM/DD/YYYY while typing
    let cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length >= 2) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 5) {
      cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5, 9);
    }
    
    setFormData(prev => ({ ...prev, [field]: cleaned }));
  };

  const convertToISODate = (mmddyyyy) => {
    if (!mmddyyyy || mmddyyyy.length !== 10) return '';
    const [month, day, year] = mmddyyyy.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.registration_type || !formData.dba_name || !formData.registration_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate date formats
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (formData.registration_date && !dateRegex.test(formData.registration_date)) {
      toast.error('Registration date must be in MM/DD/YYYY format');
      return;
    }
    if (formData.expiration_date && formData.expiration_date.length > 0 && !dateRegex.test(formData.expiration_date)) {
      toast.error('Expiration date must be in MM/DD/YYYY format');
      return;
    }

    setLoading(true);
    try {
      // Build agent address
      const agentAddress = [
        formData.agent_address,
        formData.agent_city,
        formData.agent_state,
        formData.agent_zip
      ].filter(Boolean).join(', ');

      // Build principal address
      const principalAddress = [
        formData.principal_address,
        formData.principal_city,
        formData.principal_state,
        formData.principal_zip
      ].filter(Boolean).join(', ');

      const payload = {
        entity_id: 'ENT-LOSSPAY-001',
        state: state,
        dba_name: formData.dba_name,
        registration_type: formData.registration_type,
        registration_number: formData.registration_number || null,
        registration_date: convertToISODate(formData.registration_date),
        expiration_date: formData.expiration_date ? convertToISODate(formData.expiration_date) : null,
        registration_fee: formData.registration_fee ? parseFloat(formData.registration_fee) : null,
        registered_agent: formData.registered_agent || null,
        agent_address: agentAddress || null,
        principal_address: principalAddress || null,
        status: formData.status,
        owner: 'admin@losspay.com'
      };

      await api.createDBA(payload);
      
      // Auto-create expense if fee exists
      if (formData.registration_fee && parseFloat(formData.registration_fee) > 0) {
        try {
          const expensePayload = {
            entity_id: 'ENT-LOSSPAY-001',
            title: `${formData.registration_type} - ${formData.dba_name}`,
            description: `${formData.registration_type} registration fee for ${formData.dba_name} in ${state}`,
            state: state,
            category: formData.registration_type === 'DBA' ? 'DBA Registration' : 'Foreign Qualification',
            amount: parseFloat(formData.registration_fee),
            payment_date: convertToISODate(formData.registration_date),
            status: 'paid'
          };
          await api.createExpense(expensePayload);
          console.log('Expense created for registration fee:', expensePayload);
        } catch (expenseError) {
          console.error('Failed to create expense entry:', expenseError);
          // Don't fail the registration if expense fails
        }
      }
      
      toast.success(`${formData.registration_type} registered successfully for ${state}`);
      
      // Reset form
      setFormData({
        registration_type: '',
        dba_name: '',
        registration_number: '',
        registration_date: '',
        expiration_date: '',
        registration_fee: '',
        registered_agent: '',
        agent_address: '',
        agent_city: '',
        agent_state: '',
        agent_zip: '',
        principal_address: '',
        principal_city: '',
        principal_state: '',
        principal_zip: '',
        status: 'active'
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create registration:', error);
      toast.error('Failed to create registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-registration-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Building className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <DialogTitle className="text-xl">Add Ancillary Registration</DialogTitle>
              <DialogDescription className="mt-1">
                Add a new registration for <Badge variant="secondary" className="ml-1">{state}</Badge>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Registration Type */}
          <div className="space-y-2">
            <Label htmlFor="registration-type" className="text-sm font-medium">
              Registration Type <span className="text-rose-500">*</span>
            </Label>
            <Select 
              value={formData.registration_type} 
              onValueChange={(value) => handleChange('registration_type', value)}
              data-testid="registration-type-select"
            >
              <SelectTrigger id="registration-type" className="w-full">
                <SelectValue placeholder="Select registration type" />
              </SelectTrigger>
              <SelectContent>
                {registrationTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Select the type of registration</p>
          </div>

          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="dba-name" className="text-sm font-medium">
              {formData.registration_type === 'Registered Agent' ? 'Agent Name' : 
               formData.registration_type === 'Foreign Corporation' ? 'Corporation Name' : 
               'DBA Name'} <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="dba-name"
              type="text"
              value={formData.dba_name}
              onChange={(e) => handleChange('dba_name', e.target.value)}
              placeholder="Enter name"
              data-testid="dba-name-input"
            />
            <p className="text-xs text-muted-foreground">Official registered name</p>
          </div>

          {/* Registration Number and Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="registration-number" className="text-sm font-medium">
                Registration Number
              </Label>
              <Input
                id="registration-number"
                type="text"
                value={formData.registration_number}
                onChange={(e) => handleChange('registration_number', e.target.value)}
                placeholder="e.g., 123456"
                data-testid="registration-number-input"
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration-date" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Registration Date <span className="text-rose-500">*</span>
              </Label>
              <DatePicker
                id="registration-date"
                value={formData.registration_date}
                onChange={(value) => handleChange('registration_date', value)}
                placeholder="MM/DD/YYYY"
                data-testid="registration-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiration-date" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Expiration Date
              </Label>
              <DatePicker
                id="expiration-date"
                value={formData.expiration_date}
                onChange={(value) => handleChange('expiration_date', value)}
                placeholder="MM/DD/YYYY"
                data-testid="expiration-date-input"
              />
            </div>
          </div>

          {/* Registration Fee */}
          <div className="space-y-2">
            <Label htmlFor="registration-fee" className="text-sm font-medium">
              Registration Fee
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="registration-fee"
                type="number"
                step="0.01"
                min="0"
                value={formData.registration_fee}
                onChange={(e) => handleChange('registration_fee', e.target.value)}
                placeholder="0.00"
                data-testid="registration-fee-input"
                className="pl-7"
              />
            </div>
            <p className="text-xs text-muted-foreground">Enter the registration filing fee amount</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              data-testid="submit-registration-button"
              className="w-full sm:w-auto"
            >
              {loading ? 'Adding Registration...' : 'Add Registration'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRegistrationDialog;
